import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCan } from '@/hooks/useCan'
import type { EstatePart, PartRuleKind } from '../api'
import { useDeleteEstatePart, useEstatePartOptions, useEstateParts, useOrderEstateParts, useSaveEstatePart } from '../estate-parts'

/**
 * The parts of the estate a recovery scenario treats separately: mainframe, distributed servers,
 * already in cloud, SaaS... Each is defined by rules - a classification, a platform, a location, or
 * any SaaS platform - and the FIRST part in this order whose rules match an object holds it. An
 * object no rule matches belongs to the part of what it runs on.
 */
const problemOf = (error: unknown) =>
    (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
    || (error as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (error as Error)?.message || null

type Rule = { kind: PartRuleKind; refId: number; name: string }
const KINDS: Exclude<PartRuleKind, 'saas'>[] = ['classification', 'technology', 'location']
const SHOWN = 8

export function EstatePartsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
    const { t } = useTranslation()
    const canChange = useCan('recover-requests.update')
    const canDelete = useCan('recover-requests.delete')
    const { data: parts = [], isLoading } = useEstateParts(open)
    const order = useOrderEstateParts()
    const remove = useDeleteEstatePart()
    const [editing, setEditing] = useState<EstatePart | 'new' | null>(null)
    const [problem, setProblem] = useState<string | null>(null)

    const move = (index: number, by: -1 | 1) => {
        const ids = parts.map((p) => p.id)
        const [id] = ids.splice(index, 1)
        ids.splice(index + by, 0, id)
        setProblem(null)
        order.mutate(ids, { onError: (e) => setProblem(problemOf(e)) })
    }

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) { setEditing(null); setProblem(null) } onOpenChange(next) }}>
            <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
                {editing ? (
                    <PartForm part={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('recoveryScenarios.parts.manageTitle', 'Parts of the estate')}</DialogTitle>
                            <DialogDescription>{t('recoveryScenarios.parts.manageHelp', 'Each scenario says where each of these goes. An object belongs to the first part, in this order, whose rules match it; failing that, to the part of what it runs on.')}</DialogDescription>
                        </DialogHeader>
                        {problem && <p role="alert" className="text-sm font-medium text-destructive">{problem}</p>}
                        {isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p> : (
                            <ol className="divide-y rounded-lg border" data-testid="estate-parts">
                                {parts.map((part, i) => (
                                    <li key={part.id} className="flex items-start gap-3 p-3" data-testid={`estate-part-${part.id}`}>
                                        <span className="mt-0.5 w-5 text-right text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium">{part.name} <span className="text-xs font-normal text-muted-foreground">· {t('recoveryScenarios.parts.objects', '{{count}} objects', { count: part.objects })}</span></p>
                                            {part.description && <p className="text-xs text-muted-foreground">{part.description}</p>}
                                            <p className="mt-1 text-xs">
                                                {part.rules.length
                                                    ? part.rules.map((r) => r.name).join(' · ')
                                                    : <span className="text-amber-700 dark:text-amber-400">{t('recoveryScenarios.parts.noRules', 'No rules: only objects running on this part\'s objects can be in it.')}</span>}
                                            </p>
                                        </div>
                                        {canChange && (
                                            <div className="flex shrink-0 items-center gap-1">
                                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0 || order.isPending} aria-label={t('recoveryScenarios.parts.moveUp', 'Move {{name}} up', { name: part.name })} onClick={() => move(i, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
                                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === parts.length - 1 || order.isPending} aria-label={t('recoveryScenarios.parts.moveDown', 'Move {{name}} down', { name: part.name })} onClick={() => move(i, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
                                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" aria-label={t('recoveryScenarios.parts.edit', 'Change {{name}}', { name: part.name })} onClick={() => setEditing(part)}><Pencil className="h-3.5 w-3.5" /></Button>
                                                {canDelete && (
                                                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" aria-label={t('recoveryScenarios.parts.delete', 'Remove {{name}}', { name: part.name })}
                                                        onClick={() => { setProblem(null); remove.mutate(part.id, { onError: (e) => setProblem(problemOf(e)) }) }}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                ))}
                                {parts.length === 0 && <li className="p-3 text-sm text-muted-foreground">{t('recoveryScenarios.parts.none', 'No parts yet.')}</li>}
                            </ol>
                        )}
                        <DialogFooter>
                            {canChange && <Button type="button" variant="outline" onClick={() => setEditing('new')} data-testid="estate-part-add"><Plus className="mr-1.5 h-4 w-4" />{t('recoveryScenarios.parts.add', 'Add a part')}</Button>}
                            <Button type="button" onClick={() => onOpenChange(false)}>{t('common.close', 'Close')}</Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

function PartForm({ part, onDone }: { part: EstatePart | null; onDone: () => void }) {
    const { t } = useTranslation()
    const { data: options } = useEstatePartOptions(true)
    const save = useSaveEstatePart()
    const [name, setName] = useState(part?.name ?? '')
    const [description, setDescription] = useState(part?.description ?? '')
    const [rules, setRules] = useState<Rule[]>(part?.rules ?? [])
    const [kind, setKind] = useState<Exclude<PartRuleKind, 'saas'>>('classification')
    const [search, setSearch] = useState('')
    const [problem, setProblem] = useState<string | null>(null)
    const kindLabel = (k: PartRuleKind) => ({
        classification: t('recoveryScenarios.parts.ruleClassification', 'Classification'),
        technology: t('recoveryScenarios.parts.rulePlatform', 'Platform'),
        location: t('recoveryScenarios.parts.ruleLocation', 'Location'),
        saas: t('recoveryScenarios.parts.ruleSaas', 'Any SaaS platform'),
    })[k]
    const has = (k: PartRuleKind, refId: number) => rules.some((r) => r.kind === k && r.refId === refId)
    const matches = useMemo(() => {
        const words = search.trim().toLowerCase()
        return (options?.[kind] ?? []).filter((o) => !has(kind, o.refId) && (!words || o.name.toLowerCase().includes(words)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, kind, search, rules])
    const saas = has('saas', 0)

    const submit = () => {
        setProblem(null)
        save.mutate(
            { id: part?.id ?? null, body: { name: name.trim(), description: description.trim(), rules: rules.map(({ kind: k, refId }) => ({ kind: k, refId })) } },
            { onSuccess: onDone, onError: (e) => setProblem(problemOf(e)) },
        )
    }

    return (
        <>
            <DialogHeader>
                <DialogTitle>{part ? t('recoveryScenarios.parts.editTitle', 'Change "{{name}}"', { name: part.name }) : t('recoveryScenarios.parts.add', 'Add a part')}</DialogTitle>
                <DialogDescription>{t('recoveryScenarios.parts.rulesHelp', 'An object is in this part when any one of these rules matches it.')}</DialogDescription>
            </DialogHeader>
            <label className="block space-y-1 text-sm font-medium">
                {t('recoveryScenarios.name', 'Name')}
                <Input value={name} maxLength={100} onChange={(e) => setName(e.target.value)} placeholder={t('recoveryScenarios.parts.nameExample', 'For example: Midrange (AIX, IBM i)')} data-testid="estate-part-name" />
            </label>
            <label className="block space-y-1 text-sm font-medium">
                {t('recoveryScenarios.description', 'Description')}
                <Textarea value={description} rows={2} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <div className="space-y-2">
                <Label>{t('recoveryScenarios.parts.rules', 'Rules')}</Label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={saas} onChange={() => setRules(saas ? rules.filter((r) => r.kind !== 'saas') : [...rules, { kind: 'saas', refId: 0, name: kindLabel('saas') }])} />
                    {t('recoveryScenarios.parts.ruleSaasHelp', 'Any platform marked SaaS on its technology record')}
                </label>
                {rules.filter((r) => r.kind !== 'saas').length > 0 && (
                    <ul className="flex flex-wrap gap-1.5">
                        {rules.filter((r) => r.kind !== 'saas').map((r) => (
                            <li key={`${r.kind}:${r.refId}`} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-xs">
                                <span className="text-muted-foreground">{kindLabel(r.kind)}:</span>
                                <span className="font-medium">{r.name}</span>
                                <button type="button" className="rounded-full p-0.5 hover:bg-muted" aria-label={t('recoveryScenarios.dependsOn.remove', 'Remove {{name}}', { name: r.name })} onClick={() => setRules(rules.filter((x) => !(x.kind === r.kind && x.refId === r.refId)))}>
                                    <X className="h-3 w-3" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="flex gap-2">
                    <Select value={kind} onValueChange={(next) => { setKind(next as typeof kind); setSearch('') }}>
                        <SelectTrigger className="h-9 w-44" aria-label={t('recoveryScenarios.parts.ruleKind', 'Kind of rule')}><SelectValue /></SelectTrigger>
                        <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{kindLabel(k)}</SelectItem>)}</SelectContent>
                    </Select>
                    <label className="relative min-w-0 flex-1">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('recoveryScenarios.parts.ruleSearch', 'Search for a rule to add')} placeholder={t('common.search', 'Search')} className="h-9 w-full rounded-md border bg-background pl-8 pr-2 text-sm" />
                    </label>
                </div>
                <ul className="max-h-48 overflow-y-auto rounded-md border text-sm" data-testid="estate-part-options">
                    {matches.slice(0, SHOWN).map((o) => (
                        <li key={o.refId}>
                            <button type="button" className="w-full truncate px-2 py-1 text-left hover:bg-muted" onClick={() => setRules([...rules, { kind, refId: o.refId, name: o.name }])}>{o.name}</button>
                        </li>
                    ))}
                    {matches.length === 0 && <li className="px-2 py-1 text-xs text-muted-foreground">{options ? t('recoveryScenarios.dependsOn.none', 'Nothing to add matches.') : t('common.loading', 'Loading...')}</li>}
                    {matches.length > SHOWN && <li className="px-2 py-1 text-xs text-muted-foreground">{t('recoveryScenarios.dependsOn.more', '{{count}} more: search to narrow it.', { count: matches.length - SHOWN })}</li>}
                </ul>
            </div>
            {problem && <p role="alert" className="text-sm font-medium text-destructive">{problem}</p>}
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onDone}>{t('common.cancel', 'Cancel')}</Button>
                <Button type="button" onClick={submit} disabled={!name.trim() || save.isPending} data-testid="estate-part-save">{t('common.save', 'Save')}</Button>
            </DialogFooter>
        </>
    )
}
